import { FC, useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react-lite';

import { fetchTraderInfo, TraderInfoApiResponse } from 'api/galaxyApi';
import {
  DetailBadge,
  DetailBody,
  DetailCloseButton,
  DetailHeader,
  DetailPanel,
  PanelType,
} from 'components';
import { useFollow } from 'hooks/meme/useFollow';
import { useUnfollow } from 'hooks/meme/useUnfollow';
import { store } from 'store';
import { formatCurrency, formatWalletAddress } from 'utils/formatters';
import { Address } from 'viem';

import * as S from './TraderDetailPanel.styles';

const PROFILE_BASE_URL = 'https://app.wowmax.exchange/copy-trading/137';

const badgeText = (type: string) => {
  if (type === 'profit') return '✦ PROFIT';
  if (type === 'loss') return '✦ LOSS';
  return '◉ INFO';
};

const formatRoi = (
  value: number | null | undefined
): { text: string; type: 'profit' | 'loss' | 'neutral' } => {
  if (value === null || value === undefined) {
    return { text: 'N/A', type: 'neutral' };
  }
  const pct = Math.round(value * 100);
  const sign = pct >= 0 ? '+' : '';
  return { text: `${sign}${pct}%`, type: pct >= 0 ? 'profit' : 'loss' };
};

export const TraderDetailPanel: FC = observer(() => {
  const { selectedTraderIndex, closeTraderCard, guestStar } = store.galaxyStore;
  const { getTraderByIndex } = store.galaxyStore;
  const { account } = store.web3Store;
  const { wallets: followedWallets } = store.portfolioStore;

  const follow = useFollow();
  const unfollow = useUnfollow();
  const [isFollowSubmitting, setIsFollowSubmitting] = useState(false);

  const trader = useMemo(() => {
    let star:
      | {
          wallet: string;
          totalTrades: number;
          pnl: number;
          f_roi: number;
          mind: { archetype: string | null } | null;
        }
      | undefined;
    if (selectedTraderIndex !== null) {
      star = getTraderByIndex(selectedTraderIndex) ?? undefined;
    } else if (guestStar) {
      star = guestStar;
    }
    if (!star) return undefined;
    return {
      wallet: star.wallet,
      amount: star.totalTrades,
      amountLabel: 'POSITIONS',
      pnl: star.pnl,
      roi: star.f_roi,
      archetype: star.mind?.archetype ?? null,
      type: 'info' as const,
    };
  }, [selectedTraderIndex, guestStar, getTraderByIndex]);

  const isActive = selectedTraderIndex !== null || guestStar !== null;

  const [freshData, setFreshData] = useState<TraderInfoApiResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const traderWallet = trader?.wallet;

  useEffect(() => {
    if (!isActive || !traderWallet) {
      setFreshData(null);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    fetchTraderInfo(traderWallet).then((data) => {
      if (data) setFreshData(data);
      setIsLoading(false);
    });
  }, [isActive, traderWallet]);

  // Reset Follow button submitting state when the panel closes or
  // switches to a different trader.
  useEffect(() => {
    setIsFollowSubmitting(false);
  }, [traderWallet]);

  // One-shot diagnostic so we can see in DevTools console why the Copy
  // / Stop Copying button might mis-detect the copying state. Logs the
  // target address (lower-cased), the connected account, the number of
  // copied leaders in portfolioStore, and the first few leader fields
  // so we can confirm both that the store is populated and that
  // address fields line up by case. Cheap (one debug call per trader
  // switch) and easy to leave in.
  useEffect(() => {
    if (!traderWallet) return;
    const target = traderWallet.toLowerCase();
    const sample = followedWallets.slice(0, 5).map((w) => ({
      address: w.address,
      addressLower: w.address?.toLowerCase(),
      matches: w.address?.toLowerCase() === target,
    }));

    console.debug('[TraderDetailPanel] copy-state diagnostics', {
      target,
      account,
      followedCount: followedWallets.length,
      sample,
    });
  }, [traderWallet, followedWallets, account]);

  const isAlreadyFollowing = useMemo(() => {
    if (!traderWallet) return false;
    const target = traderWallet.toLowerCase();
    return followedWallets.some((w) => w.address?.toLowerCase() === target);
  }, [followedWallets, traderWallet]);

  const handleFollowClick = async () => {
    if (!traderWallet || isFollowSubmitting) return;

    if (!account) {
      // Surface the existing wallet-connection flow. Reusing the stored
      // toast handler avoids a hard dependency on rainbowkit hooks here.
      // The user can re-click after connecting.
      // Keep it minimal: a quick toast is enough.
      const { toast } = await import('react-toastify');
      toast.info('Connect your wallet first');
      return;
    }

    setIsFollowSubmitting(true);
    try {
      if (isAlreadyFollowing) {
        // Use safe unfollow path: any open positions copied from this
        // leader will be closed before the leader is removed.
        await unfollow([traderWallet as Address], true);
      } else {
        // useFollow already handles the "no proxy wallet yet" case
        // (creates one with zero deposit) and the "wallet exists, just
        // append leader" case, both idempotent.
        await follow([traderWallet as Address]);
      }
    } finally {
      setIsFollowSubmitting(false);
    }
  };

  if (!trader) return null;

  const panelType = trader.type as PanelType;
  const isInfo = trader.type === 'info';

  const pnl = freshData?.pnl_30d ?? trader.pnl;
  const pnlType = pnl >= 0 ? 'profit' : 'loss';

  const roi = formatRoi(freshData?.f_roi_30d ?? (isInfo ? null : trader.roi));

  const followButtonLabel = (() => {
    if (isFollowSubmitting) return isAlreadyFollowing ? 'Stopping…' : 'Copying…';
    if (isAlreadyFollowing) return 'Stop Copying';
    return 'Copy';
  })();

  return (
    <DetailPanel isActive={isActive} type={panelType}>
      <DetailHeader>
        <DetailBadge type={panelType}>{badgeText(trader.type)}</DetailBadge>
        <S.Wallet>
          <S.WalletLink
            href={`${PROFILE_BASE_URL}/${trader.wallet}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            {formatWalletAddress(trader.wallet)}
          </S.WalletLink>
        </S.Wallet>
        <DetailCloseButton onClick={closeTraderCard} />
      </DetailHeader>
      <DetailBody>
        <S.MainRow>
          <S.AmountSection>
            {trader.amountLabel && <S.Label>{trader.amountLabel}</S.Label>}
            <S.Amount
              // $variant={
              //   isInfo
              //     ? 'neutral'
              //     : trader.type === 'profit' || trader.type === 'loss'
              //       ? trader.type
              //       : 'neutral'
              // }
              $variant="neutral"
            >
              {isInfo
                ? trader.amount.toLocaleString('en-US')
                : formatCurrency(trader.amount, { maximumFractionDigits: 0 })}
            </S.Amount>
          </S.AmountSection>
          <S.PnlSection>
            <S.Label>30-DAY PNL</S.Label>
            <S.StatValue $type={isLoading ? 'neutral' : pnlType}>
              {isLoading ? '...' : formatCurrency(pnl, { maximumFractionDigits: 0 })}
            </S.StatValue>
          </S.PnlSection>
        </S.MainRow>
        {trader.archetype && (
          <S.ArchetypeSection>
            <S.Label>CONSTELLATION</S.Label>
            <S.ArchetypeValue>{trader.archetype}</S.ArchetypeValue>
          </S.ArchetypeSection>
        )}
        <S.RoiSection>
          <S.Label>FOLLOWERS' ROI</S.Label>
          <S.RoiValue $type={isLoading ? 'neutral' : roi.type}>
            {isLoading ? '...' : roi.text}
          </S.RoiValue>
        </S.RoiSection>
        <S.FollowAction>
          <S.FollowButton
            $variant={isAlreadyFollowing ? 'unfollow' : 'follow'}
            onClick={handleFollowClick}
            disabled={isFollowSubmitting}
          >
            {followButtonLabel}
          </S.FollowButton>
        </S.FollowAction>
      </DetailBody>
    </DetailPanel>
  );
});
